import { Card, Statistic, Table, Tag, Spin } from "antd";
import { Building2, TrendingUp, Users, CheckCircle, Clock, AlertTriangle } from "lucide-react";
import { useCompanyStats, useCompanyList } from "../../../api-hooks/companies";
import { TCompany } from "../../../types";
import dayjs from "dayjs";

const statusColor: Record<string, string> = {
  active: "green",
  trial: "blue",
  past_due: "orange",
  cancelled: "red",
  paused: "gray",
};

const columns = [
  { title: "Company", dataIndex: "name", key: "name", render: (n: string) => <span className="font-medium">{n}</span> },
  { title: "Plan", dataIndex: "plan_name", key: "plan_name" },
  {
    title: "Status",
    dataIndex: "subscription_status",
    key: "subscription_status",
    render: (s: string) => <Tag color={statusColor[s] || "default"}>{(s || "").replace("_", " ").toUpperCase()}</Tag>,
  },
  { title: "Users", dataIndex: "user_count", key: "user_count" },
  {
    title: "Created",
    dataIndex: "created_at",
    key: "created_at",
    render: (d: string) => d ? dayjs(d).format("DD MMM YYYY") : "—",
  },
];

export default function Dashboard() {
  const { data: stats, isLoading: statsLoading } = useCompanyStats();
  const { data: companies = [], isLoading: companiesLoading } = useCompanyList({ limit: 10 });

  const isLoading = statsLoading || companiesLoading;

  const statCards = stats
    ? [
        { label: "Total Companies", value: +stats.total_companies, icon: Building2, color: "#4c1d95" },
        { label: "Active", value: +stats.active_companies, icon: CheckCircle, color: "#16a34a" },
        { label: "Trial", value: +stats.trial_companies, icon: Clock, color: "#2563eb" },
        { label: "Suspended", value: +stats.suspended_companies, icon: AlertTriangle, color: "#dc2626" },
        { label: "Total Users", value: +stats.total_users, icon: Users, color: "#0891b2" },
        { label: "MRR", value: `£${Number(stats.mrr || 0).toLocaleString()}`, icon: TrendingUp, color: "#d97706" },
      ]
    : [];

  if (isLoading) return <div className="flex justify-center py-20"><Spin size="large" /></div>;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-slate-800">Platform Overview</h2>
        <p className="text-slate-500 text-sm mt-1">Real-time stats across all companies</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
                <Icon size={18} style={{ color }} />
              </div>
              <div>
                <Statistic value={value} valueStyle={{ fontSize: 22, fontWeight: 700, color: "#1e293b" }} />
                <p className="text-slate-500 text-xs mt-0.5">{label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card
        title="Recent Companies"
        className="rounded-2xl border border-slate-200 shadow-sm"
        styles={{ body: { padding: 0 } }}
      >
        <Table
          dataSource={companies}
          columns={columns}
          rowKey="id"
          pagination={false}
          size="middle"
        />
      </Card>
    </div>
  );
}
